/* eslint-disable jsx-a11y/anchor-is-valid */
import { useMutation } from "@apollo/client";
import { showNotification, updateNotification } from "@mantine/notifications";
import { FC, Fragment } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-location";
import { forgetPassword } from "../../graphql/mutations/auth";
import { usePage } from "../../hooks/usePage";

interface ForgetPasswordInput {
  email: string;
}

const ForgotPasswordScreen: FC = () => {
  usePage("Kava | Forgot Password");
  const navigate = useNavigate();
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<ForgetPasswordInput>();

  const [requestPasswordReset, { loading }] = useMutation(forgetPassword);

  const onSubmit: SubmitHandler<ForgetPasswordInput> = (data) => {
    showNotification({
      id: "forgot-password",
      loading: true,
      title: "Requesting Reset Link",
      message: "Checking credentials and sending link to your email...",
      autoClose: false,
      disallowClose: true,
    });

    requestPasswordReset({
      variables: {
        input: { email: data.email },
      },
    })
      .then((res) => {
        const result = res.data?.forgottenPassword;
        if (result?.status) {
          updateNotification({
            id: "forgot-password",
            title: "Success",
            message:
              result.message || "Password reset link sent to your email.",
            color: "green",
          });
          navigate({
            to: "/reset-password",
            search: { email: data.email },
            replace: true,
          });
        } else {
          updateNotification({
            id: "forgot-password",
            title: "Failed",
            message: result?.message || "Could not find account or send link.",
            color: "red",
          });
        }
      })
      .catch((error) => {
        updateNotification({
          id: "forgot-password",
          title: "Error",
          message:
            error.message || "An unexpected error occurred. Please try again.",
          color: "red",
        });
      });
  };

  return (
    <Fragment>
      <div className="flex flex-col items-center">
        <h2 className="mt-6 text-3xl font-medium text-gray-900">
          Forgot Password
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          Enter your email address and we'll send you a link to reset your
          password.
        </p>
      </div>
      <div className="mt-8 max-w-sm w-full">
        <div className="mt-6">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label
                htmlFor="identifier"
                className="block text-sm font-medium text-gray-700"
              >
                Email Address
              </label>
              <div className="mt-1">
                <input
                  type="email"
                  {...register("email", { required: "Required" })}
                  id="identifier"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm p-2 border"
                  placeholder="you@example.com"
                />
                {errors?.email && (
                  <p className="text-red-400 text-sm mt-1">
                    {errors?.email?.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border bg-white rounded-md shadow-sm text-sm font-medium text-green-600 hover:text-white border-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 transition duration-200"
              >
                Request Reset Link
                {loading && (
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
              </button>
            </div>
          </form>

          <div className="mt-4 text-center text-sm">
            <a
              onClick={() => navigate({ to: "/cpanel-login" })}
              className="font-medium text-green-600 hover:text-green-500 cursor-pointer"
            >
              Back to Login
            </a>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ForgotPasswordScreen;
